// =============================================================================
// PARTNER CAPACITY MANAGEMENT
// Track partner workload and prevent overload
// =============================================================================

import { logger } from '@/utils/logger'

// NOTE: This function now reads from localStorage cache which is populated by Supabase
// The cache is updated by refreshOrdersFromCloud() and getAllOrdersAsync()
// DEPRECATED: Use getOrdersFromSupabaseAsync() instead
// This function returns empty - Supabase is the source of truth
function getOrdersFromCache(): any[] {
  console.warn('[DEPRECATED] getOrdersFromCache() called - use getOrdersFromSupabaseAsync() instead')
  return []
}

// SUPABASE-FIRST: Async version for accurate partner capacity calculations
async function getOrdersFromSupabaseAsync(): Promise<any[]> {
  try {
    const { OrdersService } = await import('../services/cloudDataService')
    const result = await OrdersService.getAll()
    return result.data || []
  } catch {
    return []
  }
}

const log = logger.module('Partners')

const PARTNERS_STORAGE_KEY = 'blissMillworksPartners'
const PARTNER_METRICS_KEY = 'bliss_partner_metrics'

// =============================================================================
// TYPES
// =============================================================================

export interface PartnerCapacity {
  maxWeeklyOrders: number
  maxConcurrentOrders: number
  preferredLeadTimeDays: number
  bufferDays: number
  updatedAt?: string
}

export interface Partner {
  id: string
  name: string
  ownerName?: string
  city?: string
  state?: string
  region?: string
  territory?: string
  status: 'active' | 'inactive'
  since?: string
  specialties?: string[]
  location?: string
  capacity?: PartnerCapacity
}

export interface CapacityStatus {
  current: number
  max: number
  percentage: number
  status: 'available' | 'moderate' | 'busy' | 'full'
  canAcceptMore: boolean
  activeOrders: any[]
}

export interface PartnerWithCapacity extends Partner {
  capacityStatus: CapacityStatus
}

export interface PartnerMetrics {
  partnerId: string
  partnerName: string
  capacity: CapacityStatus
  activeCount: number
  completedCount: number
  averageLeadTimeDays: number | null
  completionRate: number | null
  totalEarned: number
  pendingEarnings: number
  recentCompleted: any[]
}

export interface PartnerScore {
  score: number
  metrics: PartnerMetrics
}

export interface PartnerAlert {
  partnerId: string
  partnerName: string
  current: number
  max: number
  percentage: number
  status: string
  canAcceptMore: boolean
  overloadCount?: number
}

// =============================================================================
// PARTNER DATA STRUCTURE
// =============================================================================

// Default partner capacity settings
export const DEFAULT_CAPACITY: PartnerCapacity = {
  maxWeeklyOrders: 5,
  maxConcurrentOrders: 3,
  preferredLeadTimeDays: 14,
  bufferDays: 2
}

// Default partner for initialization
const DEFAULT_PARTNER: Partner = {
  id: 'choctaw-beach',
  name: 'Bliss Millworks',
  ownerName: 'Heith R.',
  city: 'Choctaw Beach',
  state: 'FL',
  region: 'Southeast',
  territory: 'FL Panhandle, 30A, Destin, Panama City',
  status: 'active',
  since: '2023',
  specialties: ['Standard Bunks', 'Triple Bunks', 'Custom Finishes'],
  location: 'Choctaw Beach, FL'
}

// Get all partners from localStorage
// In-memory cache for partners (seeded with default)
let partnersCache: Partner[] = [DEFAULT_PARTNER]

// @deprecated Use getPartnersAsync for Supabase-first
export function getPartners(): Partner[] {
  return partnersCache.map(p => ({
    ...p,
    name: p.name || `${p.ownerName || 'Partner'} - ${p.city || 'Unknown'}`,
    location: p.location || `${p.city}, ${p.state}`
  }))
}

/**
 * SUPABASE-FIRST: Get all partners from Supabase
 * Falls back to localStorage only if Supabase fails
 */
export async function getPartnersAsync(): Promise<Partner[]> {
  try {
    const { PartnersService } = await import('../services/cloudDataService')
    const result = await PartnersService.getAll()
    
    if (result.error || !result.data) {
      log.error('[getPartnersAsync] Supabase fetch failed, using localStorage fallback')
      return getPartners()
    }
    
    // Ensure each partner has a 'name' field for display
    return result.data.map((p: any) => ({
      ...p,
      name: p.name || `${p.ownerName || 'Partner'} - ${p.city || 'Unknown'}`,
      location: p.location || `${p.city}, ${p.state}`
    })) as Partner[]
  } catch (e) {
    log.error('[getPartnersAsync] Error:', e)
    return getPartners()
  }
}

/**
 * SUPABASE-FIRST: Get a single partner by ID from Supabase
 */
export async function getPartnerByIdAsync(partnerId: string): Promise<Partner | null> {
  try {
    const { PartnersService } = await import('../services/cloudDataService')
    const result = await PartnersService.getById(partnerId)
    
    if (result.error || !result.data) {
      log.warn(`[getPartnerByIdAsync] Partner ${partnerId} not found in Supabase, trying localStorage`)
      return getPartnerById(partnerId)
    }
    
    const p = result.data
    return {
      ...p,
      name: p.name || `${p.ownerName || 'Partner'} - ${p.city || 'Unknown'}`,
      location: p.location || `${p.city}, ${p.state}`
    } as Partner
  } catch (e) {
    log.error('[getPartnerByIdAsync] Error:', e)
    return getPartnerById(partnerId)
  }
}

// Save partners (session-only)
export function savePartners(partners: Partner[]): void {
  partnersCache = partners
}

// Get a single partner by ID (checks id, legacyId, and uuid for backward compat)
export function getPartnerById(partnerId: string): Partner | null {
  const partners = getPartners()
  return partners.find(p => 
    p.id === partnerId || 
    (p as any).legacyId === partnerId ||
    (p as any).uuid === partnerId
  ) || null
}

// =============================================================================
// PARTNER IDENTITY RESOLUTION
// Async utilities for resolving partner by any identifier (UUID, slug, name)
// =============================================================================

export async function resolvePartnerAsync(partnerId: string): Promise<Partner | null> {
  if (!partnerId) return null

  const allPartners = await getPartnersAsync()

  const partner = allPartners.find((p: any) => {
    if (p.id === partnerId) return true
    if (p.uuid === partnerId) return true
    if (p.legacyId === partnerId) return true
    if (p.name?.toLowerCase() === partnerId.toLowerCase()) return true
    const slug = p.name?.toLowerCase().replace(/\s+/g, '-')
    if (slug === partnerId.toLowerCase()) return true
    return false
  })

  return partner || null
}

export async function resolvePartnerUuidAsync(partnerId: string): Promise<string | null> {
  const partner = await resolvePartnerAsync(partnerId) as any
  return partner?.uuid || partner?.id || null
}

// Update partner capacity settings
export function updatePartnerCapacity(partnerId: string, capacitySettings: Partial<PartnerCapacity>): Partner | null {
  const partners = getPartners()
  const index = partners.findIndex(p => p.id === partnerId)

  if (index >= 0) {
    partners[index].capacity = {
      ...partners[index].capacity,
      ...capacitySettings,
      updatedAt: new Date().toISOString()
    } as PartnerCapacity
    savePartners(partners)
    return partners[index]
  }
  return null
}

// =============================================================================
// CAPACITY CALCULATIONS
// =============================================================================

/**
 * Get current active orders for a partner
 * Active = claimed or in_progress
 * CRITICAL: Must check BOTH claimedByPartner AND claimedByPartnerLocation
 * For slug-based partner IDs (e.g., "choctaw-beach"), the claim is stored in claimedByPartnerLocation
 */
export function getPartnerActiveOrders(partnerId: string): any[] {
  const orders = getOrdersFromCache()
  
  // Helper: Check if order belongs to this partner (same logic as getOrdersClaimedByPartnerAsync)
  const isClaimedByPartner = (o: any): boolean => {
    if (o.claimedByPartner === partnerId) return true
    if (o.claimedByPartnerLocation === partnerId) return true
    return false
  }
  
  // Active statuses must match portal's definition for consistency
  const activeStatuses = ['claimed', 'in_progress', 'paused', 'qc_requested', 'revision_requested']
  
  return orders.filter(o =>
    isClaimedByPartner(o) &&
    activeStatuses.includes(o.fabricationStatus)
  )
}

/**
 * Get completed orders for a partner (for historical metrics)
 * CRITICAL: Must check BOTH claimedByPartner AND claimedByPartnerLocation
 * For slug-based partner IDs (e.g., "choctaw-beach"), the claim is stored in claimedByPartnerLocation
 */
export function getPartnerCompletedOrders(partnerId: string): any[] {
  const orders = getOrdersFromCache()
  
  // Helper: Check if order belongs to this partner (same logic as getOrdersClaimedByPartnerAsync)
  const isClaimedByPartner = (o: any): boolean => {
    if (o.claimedByPartner === partnerId) return true
    if (o.claimedByPartnerLocation === partnerId) return true
    return false
  }
  
  // Completed statuses for historical metrics
  const completedStatuses = ['complete', 'ready_to_ship', 'packing', 'qc_approved']
  
  return orders.filter(o =>
    isClaimedByPartner(o) &&
    completedStatuses.includes(o.fabricationStatus)
  )
}

/**
 * Calculate partner's current capacity utilization
 * Returns: { current, max, percentage, status }
 */
export function getPartnerCapacityStatus(partnerId: string): CapacityStatus {
  const partner = getPartnerById(partnerId)
  const activeOrders = getPartnerActiveOrders(partnerId)

  const maxConcurrent = partner?.capacity?.maxConcurrentOrders || DEFAULT_CAPACITY.maxConcurrentOrders
  const current = activeOrders.length
  const percentage = Math.round((current / maxConcurrent) * 100)

  let status: CapacityStatus['status'] = 'available' // green
  if (percentage >= 100) {
    status = 'full' // red
  } else if (percentage >= 80) {
    status = 'busy' // yellow
  } else if (percentage >= 50) {
    status = 'moderate' // light green
  }

  return {
    current,
    max: maxConcurrent,
    percentage: Math.min(percentage, 100),
    status,
    canAcceptMore: current < maxConcurrent,
    activeOrders
  }
}

/**
 * SUPABASE-FIRST: Calculate partner's current capacity from Supabase
 * This is the source-of-truth version that reads directly from the database
 */
export async function getPartnerCapacityStatusAsync(partnerId: string): Promise<CapacityStatus> {
  const partner = getPartnerById(partnerId)
  const maxConcurrent = partner?.capacity?.maxConcurrentOrders || DEFAULT_CAPACITY.maxConcurrentOrders
  
  try {
    // Dynamic import to avoid circular dependency
    const { getOrdersClaimedByPartnerAsync } = await import('./orders')
    const activeOrders = await getOrdersClaimedByPartnerAsync(partnerId)
    
    const current = activeOrders.length
    const percentage = Math.round((current / maxConcurrent) * 100)

    let status: CapacityStatus['status'] = 'available'
    if (percentage >= 100) {
      status = 'full'
    } else if (percentage >= 80) {
      status = 'busy'
    } else if (percentage >= 50) {
      status = 'moderate'
    }

    return {
      current,
      max: maxConcurrent,
      percentage: Math.min(percentage, 100),
      status,
      canAcceptMore: current < maxConcurrent,
      activeOrders
    }
  } catch (err) {
    log.error('[getPartnerCapacityStatusAsync] Failed to fetch from Supabase:', err)
    // Fallback to localStorage version
    return getPartnerCapacityStatus(partnerId)
  }
}

/**
 * Check if partner can accept a new order
 * @deprecated Use canPartnerAcceptOrderAsync for Supabase-first
 */
export function canPartnerAcceptOrder(partnerId: string): boolean {
  const capacity = getPartnerCapacityStatus(partnerId)
  return capacity.canAcceptMore
}

/**
 * SUPABASE-FIRST: Check if partner can accept a new order
 */
export async function canPartnerAcceptOrderAsync(partnerId: string): Promise<boolean> {
  const capacity = await getPartnerCapacityStatusAsync(partnerId)
  return capacity.canAcceptMore
}

/**
 * SUPABASE-FIRST BATCH: Fetch orders ONCE, compute capacity for ALL partners.
 * Eliminates the N+1 query problem where each partner triggered a full orders table scan.
 * Returns a map of partnerId → CapacityStatus.
 */
export async function getCapacityBatchAsync(
  partnerIds: string[]
): Promise<Record<string, CapacityStatus>> {
  if (partnerIds.length === 0) return {}

  // Single fetch of all orders from Supabase
  const allOrders = await getOrdersFromSupabaseAsync()

  // Import partner identity check (once, not per-partner)
  const { isOrderClaimedByPartner } = await import('../services/PartnerIdentityService')

  const activeStatuses = [
    'claimed', 'in_progress', 'paused', 'qc_requested',
    'revision_requested', 'qc_approved', 'packing', 'ready_to_ship'
  ]

  const result: Record<string, CapacityStatus> = {}

  for (const partnerId of partnerIds) {
    const partner = getPartnerById(partnerId)
    const maxConcurrent = partner?.capacity?.maxConcurrentOrders || DEFAULT_CAPACITY.maxConcurrentOrders

    const activeOrders = allOrders.filter(
      (o: any) =>
        isOrderClaimedByPartner(o, partnerId) &&
        o.fabricationStatus &&
        activeStatuses.includes(o.fabricationStatus)
    )

    const current = activeOrders.length
    const percentage = Math.round((current / maxConcurrent) * 100)

    let status: CapacityStatus['status'] = 'available'
    if (percentage >= 100) status = 'full'
    else if (percentage >= 80) status = 'busy'
    else if (percentage >= 50) status = 'moderate'

    result[partnerId] = {
      current,
      max: maxConcurrent,
      percentage: Math.min(percentage, 100),
      status,
      canAcceptMore: current < maxConcurrent,
      activeOrders
    }
  }

  return result
}

/**
 * Get all partners with their capacity status
 * @deprecated Use getPartnersWithCapacityAsync for Supabase-first
 */
export function getPartnersWithCapacity(): PartnerWithCapacity[] {
  const partners = getPartners()

  return partners.map(partner => ({
    ...partner,
    capacityStatus: getPartnerCapacityStatus(partner.id)
  }))
}

/**
 * SUPABASE-FIRST: Get all partners with their capacity status from Supabase
 */
export async function getPartnersWithCapacityAsync(): Promise<PartnerWithCapacity[]> {
  const partners = getPartners()
  
  const results = await Promise.all(
    partners.map(async partner => ({
      ...partner,
      capacityStatus: await getPartnerCapacityStatusAsync(partner.id)
    }))
  )
  
  return results
}

// =============================================================================
// SUPABASE-FIRST ASYNC FUNCTIONS
// =============================================================================

/**
 * SUPABASE-FIRST: Get active orders for a partner from Supabase
 */
export async function getPartnerActiveOrdersAsync(partnerId: string): Promise<any[]> {
  try {
    const { getOrdersClaimedByPartnerAsync } = await import('./orders')
    return await getOrdersClaimedByPartnerAsync(partnerId)
  } catch (err) {
    log.error('[getPartnerActiveOrdersAsync] Failed:', err)
    return []
  }
}

/**
 * SUPABASE-FIRST: Get completed orders for a partner from Supabase
 */
export async function getPartnerCompletedOrdersAsync(partnerId: string): Promise<any[]> {
  try {
    const { getCompletedOrdersByPartnerAsync } = await import('./orders')
    return await getCompletedOrdersByPartnerAsync(partnerId)
  } catch (err) {
    log.error('[getPartnerCompletedOrdersAsync] Failed:', err)
    return []
  }
}

/**
 * SUPABASE-FIRST: Calculate average lead time for a partner
 */
export async function getPartnerAverageLeadTimeAsync(partnerId: string): Promise<number | null> {
  const completedOrders = await getPartnerCompletedOrdersAsync(partnerId)

  if (completedOrders.length === 0) {
    return null
  }

  const leadTimes = completedOrders
    .filter(o => o.claimedByPartnerAt && o.fabricationCompletedAt)
    .map(o => {
      const start = new Date(o.claimedByPartnerAt)
      const end = new Date(o.fabricationCompletedAt)
      return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    })

  if (leadTimes.length === 0) return null

  const average = leadTimes.reduce((sum, lt) => sum + lt, 0) / leadTimes.length
  return Math.round(average)
}

/**
 * SUPABASE-FIRST: Calculate partner completion rate
 */
export async function getPartnerCompletionRateAsync(partnerId: string): Promise<number | null> {
  const completedOrders = await getPartnerCompletedOrdersAsync(partnerId)

  if (completedOrders.length === 0) {
    return null
  }

  const onTime = completedOrders.filter(o => {
    if (!o.dueDate || !o.fabricationCompletedAt) return true
    return new Date(o.fabricationCompletedAt) <= new Date(o.dueDate)
  }).length

  return Math.round((onTime / completedOrders.length) * 100)
}

/**
 * SUPABASE-FIRST: Get comprehensive partner metrics from Supabase
 */
export async function getPartnerMetricsAsync(partnerId: string): Promise<PartnerMetrics> {
  const partner = getPartnerById(partnerId)
  const [activeOrders, completedOrders, capacityStatus, avgLeadTime, completionRate] = await Promise.all([
    getPartnerActiveOrdersAsync(partnerId),
    getPartnerCompletedOrdersAsync(partnerId),
    getPartnerCapacityStatusAsync(partnerId),
    getPartnerAverageLeadTimeAsync(partnerId),
    getPartnerCompletionRateAsync(partnerId)
  ])

  const totalEarned = completedOrders.reduce((sum, o) => sum + (o.fabPayoutAmount || o.fabPayout || 0), 0)
  const pendingEarnings = activeOrders.reduce((sum, o) => sum + (o.fabPayoutAmount || o.fabPayout || 0), 0)

  return {
    partnerId,
    partnerName: partner?.name || 'Unknown',
    capacity: capacityStatus,
    activeCount: activeOrders.length,
    completedCount: completedOrders.length,
    averageLeadTimeDays: avgLeadTime,
    completionRate: completionRate,
    totalEarned,
    pendingEarnings,
    recentCompleted: completedOrders.slice(0, 10)
  }
}

/**
 * SUPABASE-FIRST: Score partner for order assignment
 */
export async function scorePartnerForOrderAsync(partnerId: string, order: any): Promise<number> {
  const metrics = await getPartnerMetricsAsync(partnerId)
  let score = 100

  if (!metrics.capacity.canAcceptMore) {
    return 0
  }
  score -= metrics.capacity.percentage * 0.3

  if (metrics.completionRate !== null) {
    score += (metrics.completionRate - 80) * 0.2
  }

  if (metrics.averageLeadTimeDays !== null) {
    const leadTimeBonus = Math.max(0, 14 - metrics.averageLeadTimeDays) * 2
    score += leadTimeBonus
  }

  const experienceBonus = Math.min(metrics.completedCount, 20)
  score += experienceBonus

  return Math.max(0, Math.round(score))
}

// =============================================================================
// HISTORICAL METRICS (DEPRECATED - Use async versions above)
// =============================================================================

/**
 * Calculate average lead time for a partner
 * Based on completed orders
 */
export function getPartnerAverageLeadTime(partnerId: string): number | null {
  const completedOrders = getPartnerCompletedOrders(partnerId)

  if (completedOrders.length === 0) {
    return null
  }

  const leadTimes = completedOrders
    .filter(o => o.claimedByPartnerAt && o.fabricationCompletedAt)
    .map(o => {
      const start = new Date(o.claimedByPartnerAt)
      const end = new Date(o.fabricationCompletedAt)
      return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    })

  if (leadTimes.length === 0) return null

  const average = leadTimes.reduce((sum, lt) => sum + lt, 0) / leadTimes.length
  return Math.round(average)
}

/**
 * Calculate partner completion rate
 * (completed on time / total completed)
 */
export function getPartnerCompletionRate(partnerId: string): number | null {
  const completedOrders = getPartnerCompletedOrders(partnerId)

  if (completedOrders.length === 0) {
    return null
  }

  const onTime = completedOrders.filter(o => {
    if (!o.dueDate || !o.fabricationCompletedAt) return true // assume on time if no due date
    return new Date(o.fabricationCompletedAt) <= new Date(o.dueDate)
  }).length

  return Math.round((onTime / completedOrders.length) * 100)
}

/**
 * Get comprehensive partner metrics
 */
export function getPartnerMetrics(partnerId: string): PartnerMetrics {
  const partner = getPartnerById(partnerId)
  const activeOrders = getPartnerActiveOrders(partnerId)
  const completedOrders = getPartnerCompletedOrders(partnerId)
  const capacityStatus = getPartnerCapacityStatus(partnerId)
  const avgLeadTime = getPartnerAverageLeadTime(partnerId)
  const completionRate = getPartnerCompletionRate(partnerId)

  // Calculate earnings
  const totalEarned = completedOrders.reduce((sum, o) => sum + (o.fabPayoutAmount || 0), 0)
  const pendingEarnings = activeOrders.reduce((sum, o) => sum + (o.fabPayoutAmount || 0), 0)

  return {
    partnerId,
    partnerName: partner?.name || 'Unknown',

    // Capacity
    capacity: capacityStatus,

    // Volume
    activeCount: activeOrders.length,
    completedCount: completedOrders.length,

    // Performance
    averageLeadTimeDays: avgLeadTime,
    completionRate: completionRate, // percentage

    // Earnings
    totalEarned,
    pendingEarnings,

    // Historical orders (last 10)
    recentCompleted: completedOrders.slice(0, 10)
  }
}

// =============================================================================
// PARTNER RANKING & RECOMMENDATIONS
// =============================================================================

/**
 * Score partners for order assignment
 * Higher score = better fit for new order
 */
export function scorePartnerForOrder(partnerId: string, order: any): number {
  const metrics = getPartnerMetrics(partnerId)
  let score = 100

  // Penalize for capacity
  if (!metrics.capacity.canAcceptMore) {
    return 0 // Can't accept
  }
  score -= metrics.capacity.percentage * 0.3 // Up to -30 for being busy

  // Bonus for completion rate
  if (metrics.completionRate !== null) {
    score += (metrics.completionRate - 80) * 0.2 // +4 for 100%, -4 for 60%
  }

  // Bonus for faster lead time
  if (metrics.averageLeadTimeDays !== null) {
    const leadTimeBonus = Math.max(0, 14 - metrics.averageLeadTimeDays) * 2
    score += leadTimeBonus // Up to +14 for 7-day lead time
  }

  // Bonus for experience (completed orders)
  const experienceBonus = Math.min(metrics.completedCount, 20)
  score += experienceBonus

  return Math.max(0, Math.round(score))
}

/**
 * Get ranked list of partners for an order
 */
export function getRankedPartnersForOrder(order: any, eligiblePartners: Partner[]): Array<Partner & PartnerScore> {
  return eligiblePartners
    .map(partner => ({
      ...partner,
      score: scorePartnerForOrder(partner.id, order),
      metrics: getPartnerMetrics(partner.id)
    }))
    .filter(p => p.score > 0)
    .sort((a, b) => b.score - a.score)
}

// =============================================================================
// CAPACITY WARNINGS
// =============================================================================

/**
 * Get partners that are at or near capacity
 */
export function getPartnersNearCapacity(): PartnerAlert[] {
  const partnersWithCapacity = getPartnersWithCapacity()

  return partnersWithCapacity.filter(p =>
    p.capacityStatus.percentage >= 80
  ).map(p => ({
    partnerId: p.id,
    partnerName: p.name,
    ...p.capacityStatus
  }))
}

/**
 * Get partners that are overloaded (over 100%)
 */
export function getOverloadedPartners(): PartnerAlert[] {
  const partnersWithCapacity = getPartnersWithCapacity()

  return partnersWithCapacity.filter(p =>
    p.capacityStatus.current > p.capacityStatus.max
  ).map(p => ({
    partnerId: p.id,
    partnerName: p.name,
    ...p.capacityStatus,
    overloadCount: p.capacityStatus.current - p.capacityStatus.max
  }))
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  getPartners,
  savePartners,
  getPartnerById,
  updatePartnerCapacity,
  getPartnerActiveOrders,
  getPartnerCompletedOrders,
  getPartnerCapacityStatus,
  canPartnerAcceptOrder,
  getPartnersWithCapacity,
  getPartnerAverageLeadTime,
  getPartnerCompletionRate,
  getPartnerMetrics,
  scorePartnerForOrder,
  getRankedPartnersForOrder,
  getPartnersNearCapacity,
  getOverloadedPartners,
  DEFAULT_CAPACITY
}
